<template>
  <div class="row answer-editor">
    <div class="col-12">
      <card shadow type="secondary" class="answer-editor-card mt-4">
        <!-- CARD HEADER -->
        <template slot="header">
          <div class="row align-middle">
            <div class="col-10">
              <h4 class="mb-0">
                {{
                  $t(
                    'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.suggested-answer',
                  )
                }}
              </h4>
            </div>

            <div class="col-2 text-right" v-if="hasZendesk">
              <not-allowed-tooltip
                :user-permissions="userPermissions"
                permission-key="canUpdateContent"
              >
                <template v-slot:main-content="{ hasPermissions }">
                  <el-tooltip
                    :disabled="!hasPermissions"
                    :content="
                      $t(
                        'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.tooltip',
                      )
                    "
                    placement="left"
                    class="group-tooltip"
                  >
                    <base-switch
                      :disabled="!hasPermissions"
                      onText="✒️"
                      offText="🤖"
                      v-model="isAnswer"
                    />
                  </el-tooltip>
                </template>
              </not-allowed-tooltip>
            </div>
          </div>
        </template>
        <WarningCallout warning="actions" />
        <div v-show="isAnswer">
          <div v-if="hasZendesk" class="text-right">
            <div class="row mt-1 mb-1">
              <div class="col-1 text-center mr-1 ml-1">
                <img src="~assets/answer-icon.png" width="50px" />
              </div>
              <input
                class="subject-input col-10"
                :placeholder="
                  $t(
                    'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.subject',
                  )
                "
                :disabled="!canUpdateContent"
                v-model="updated.subject"
                @blur="updateActions({ delayed: true })"
                @keyup.enter="$event.target.blur()"
              />
            </div>
            <hr class="col-11 mb-1 mt-1" />
            <small
              class="panel-button pr-2"
              @click="zendeskPanel = !zendeskPanel"
            >
              {{
                $t(
                  'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.zendesk-attributes',
                )
              }}
            </small>
          </div>
          <div class="d-flex">
            <MaydayDiagnosticMessageEditor
              ref="editor"
              :class="{
                'col-12': !zendeskPanel,
                'col-8': zendeskPanel,
              }"
              :editable="canUpdateContent"
              :contentId="nodeId"
              :body="actionMessage"
              @input="updateMessage"
            />
            <div v-if="zendeskPanel" class="zendesk-features col-4">
              <div class="feature-list">
                <div
                  class="d-flex mb-1 mt-1"
                  v-for="feature in zendeskFeaturesPanel"
                  :key="feature.label"
                >
                  <el-tooltip
                    :content="feature.tooltipContent"
                    placement="left"
                  >
                    <label>{{ feature.label }}</label>
                  </el-tooltip>
                  <input
                    v-autowidth="{
                      maxWidth: '200px',
                      minWidth: '120px',
                      comfortZone: 0,
                    }"
                    alternative
                    :placeholder="feature.inputPlaceholder"
                    :disabled="!canUpdateContent"
                    class="inputNode"
                    id="inputTypeId"
                    v-model="updated[feature.updatedField]"
                    @blur="updateActions({ delayed: true })"
                    @keyup.enter="$event.target.blur()"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MACRO COMPONENT -->
        <div v-if="!isAnswer">
          <MacroEditor
            :macro-id="actions.macro_id"
            @update-macro="updateActions"
            :user-permissions="userPermissions"
          />
        </div>
      </card>
      <TriggerEditor
        v-if="hasSalesforce"
        :action="updatedTriggerAction"
        @create="onTriggerCreate"
        @change="onTriggerChange"
        @delete="onTriggerDelete"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import MacroEditor from './MacroEditor';
import MaydayDiagnosticMessageEditor from '@/components/Editors/MaydayDiagnosticMessageEditor';
import TriggerEditor from './TriggerEditor/TriggerEditor';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';
import WarningCallout from '@/views/KnowledgeEditor/ParametricContentBuilder/ParametricBuilderSibeBar/WarningCallout.vue';

export default {
  name: 'diagnostic-builder-trigger-editor-card',
  components: {
    MaydayDiagnosticMessageEditor,
    MacroEditor,
    TriggerEditor,
    NotAllowedTooltip,
    WarningCallout,
  },
  props: {
    nodeId: String,
    actions: Object,
    triggerAction: {
      type: Object,
      default: () => ({}),
    },
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isAnswer: true,
      zendeskPanel: false,
      new_subject: '',
      titleInput: '',
      displayOptions: {},
      zendeskFeaturesPanel: [
        {
          label: '🔒',
          tooltipContent: 'Type de message',
          inputPlaceholder: 'Type de message',
          updatedField: 'type',
        },
        {
          label: '👤',
          tooltipContent: 'Collaborateur assigné',
          inputPlaceholder: 'Assigner un user',
          updatedField: 'assignee_user_id',
        },
        {
          label: '💼',
          tooltipContent: 'Groupe assigné',
          inputPlaceholder: 'Assigner un groupe',
          updatedField: 'assignee_group_id',
        },
        {
          label: '📧',
          tooltipContent: 'Email cc',
          inputPlaceholder: 'Assigner un email',
          updatedField: 'email_cc',
        },
        {
          label: '✅',
          tooltipContent: 'Statut',
          inputPlaceholder: 'Assigner un status',
          updatedField: 'status',
        },
      ],
      updatedTriggerAction: {},
      updated: {
        macro_id: '',
        subject: '',
        message: '',
        type: '',
        assignee_user_id: '',
        assignee_group_id: '',
        email_cc: '',
        status: '',
      },
    };
  },
  created() {
    this.setActionsToLocalUpdated();
  },
  mounted() {
    this.updatedTriggerAction = this.triggerAction;
  },
  computed: {
    actionMessage() {
      return this.actions.message;
    },
    isTemplate() {
      return this.$route.path.includes('templates');
    },
    canUpdateContent() {
      // UTC TEMPLATE PERMISSIONS
      if (this.isTemplate) return true;
      return Boolean(this.userPermissions.canUpdateContent);
    },
    isTriggerActionValid() {
      if (
        !this.updatedTriggerAction ||
        !this.updatedTriggerAction.id ||
        !this.updatedTriggerAction.payload ||
        !this.updatedTriggerAction.payload.attributes
      )
        return false;
      return this.updatedTriggerAction.payload.attributes
        .map(({ key, value }) => key && key !== 'md_new_condition' && value)
        .every((el) => el);
    },
    ...mapGetters(['hasZendesk', 'hasSalesforce']),
  },
  methods: {
    setActionsToLocalUpdated() {
      this.updated = {
        ...this.actions,
      };
      if (this.updated.macro_id) {
        this.isAnswer = false;
      } else {
        this.isAnswer = true;
      }
    },
    getActionType(actions) {
      if (!this.hasZendesk) return 'CUSTOM_MESSAGE';

      return actions && actions.macro_id !== ''
        ? 'ZENDESK_MACRO'
        : 'ZENDESK_MESSAGE';
    },
    updateMessage(message) {
      this.updated.message = message;
      this.updateActions({ delayed: true });
    },
    updateActions({ actions = null, delayed = false } = {}) {
      const eventName = delayed
        ? 'delayed-update-content-actions'
        : 'update-content-actions';

      const eventPayload = [
        {
          id: this.getActionType(actions),
          payload: actions ? actions : { ...this.updated, macro_id: '' },
        },
      ];
      if (this.isTriggerActionValid) {
        eventPayload.push(this.updatedTriggerAction);
      }
      this.$emit(eventName, eventPayload);
    },
    onTriggerCreate(triggerAction) {
      this.updatedTriggerAction = triggerAction;
    },
    onTriggerChange(evt) {
      this.$emit('update-content-actions', [
        {
          id: this.getActionType(this.updated),
          payload: this.updated,
        },
        evt,
      ]);
    },
    onTriggerDelete() {
      this.updatedTriggerAction = {};
      this.$emit('update-content-actions', [
        {
          id: this.getActionType(this.updated),
          payload: this.updated,
        },
      ]);
    },
  },
  watch: {
    actions() {
      this.setActionsToLocalUpdated();
    },
    triggerAction() {
      this.updatedTriggerAction = this.triggerAction;
    },
  },
  beforeDestroy() {
    this.$emit('flush-update-content');
  },
};
</script>

<style lang="scss" scoped>
.editor_row {
  margin-right: 0px !important;
  margin-left: 0px !important;
  margin-top: 10px;
}

.subject-input {
  font-size: 24px;
  line-height: 1.5;
  background-color: transparent;
  border: none;
}

.subject-input:focus {
  outline: none;
}

.editor__content {
  :deep() .ProseMirror {
    word-break: break-word;
  }
}

.editor__content {
  :deep() .ProseMirror:focus {
    word-break: break-word;
  }
}

// ZENDESK PANEL
.zendesk-features {
  border-radius: 5px;
  padding: 10px;
  min-height: 100px;
}

.feature-list {
  background-color: #f2f4f9;
  padding: 10px;
}

.inputNode {
  border: 0px;
  font-size: 14px;
  margin-left: 5px;
  background-color: transparent;
}

.inputNode:focus {
  outline: none;
}

label {
  margin-bottom: 0px;
}

.choice {
  width: 15px;
  height: 15px;
  margin-bottom: 3px;
}

.panel-button {
  color: $grey-5-mayday;
}

.panel-button:hover {
  color: $blue-mayday;
  cursor: pointer;
}
</style>
